<template>
  <div
    class="cursor-pointer d-flex flex-column quote-card qtm-border pa-4 text-secondary h-100"
    @click="onClick"
  >
    <quote-awarded-chip :order="order" :quote="quote" />
    <quote-outdated-chip class="mb-2 me-auto" :quote="quote" />
    <div class="d-flex justify-space-between">
      <div class="background qtm-h4 text-truncate" v-text="quote.vendor?.name" />
      <v-icon v-if="unreadMsg" color="primary" icon="mdi-email" />
    </div>
    <div v-if="priceGiven" class="d-flex flex-column flex-grow-1 pt-4">
      <div class="d-flex align-center ga-1">
        <span class="qtm-title">{{ totalPrice }}</span>
        <v-icon
          v-if="isLowestPrice && !quote.is_winning_quote"
          color="dark-green"
          icon="ms:star_rate"
          size="20"
        />
      </div>
      <div v-if="hasInfo" class="d-flex flex-column mt-2">
        <div v-if="orderItemCount > 0 || leadTimeCount > 0" class="d-flex align-center ga-2 qtm-body">
          <div v-if="orderItemCount > 0" class="d-flex align-center">
            <template v-if="allItemsMatched">
              <span class="text-mid-grey">All items</span>
              <v-icon class="ml-1" color="dark-green" icon="ms:check" size="20" />
            </template>
            <span v-else class="text-warning font-weight-medium">
              {{ matchedItemCount }}/{{ orderItemCount }} items
            </span>
          </div>
          <span
            v-if="orderItemCount > 0 && leadTimeCount > 0"
            class="lead-time-dot bg-mid-grey rounded-circle flex-shrink-0"
          />
          <span v-if="leadTimeCount > 0" class="text-mid-grey">{{ leadTimeText }}</span>
        </div>
      </div>
      <v-spacer />
      <div class="d-flex ga-2 mt-6">
        <div class="action-btn">
          <qtm-btn
            block
            data-test="view-quote"
            secondary
            @click="onClick"
          >
            View
          </qtm-btn>
        </div>
        <div v-if="showActions && order.canAwardQuote && !order.orderType?.fromBom" class="action-btn">
          <quote-items-btn
            block
            :order="order"
            :quote="quote"
            @update:order="$emit('update:order', $event)"
          />
        </div>
      </div>
    </div>
    <quote-response
      v-else
      :comments="quote.comments"
      compact
      :response="responseReceived"
    />
  </div>
</template>

<script setup lang="ts">
import type { Order, Quote } from '@quotetome/materials-api'
import { currency } from '@/models/filters'
import QuoteAwardedChip from '@/components/quotes/quote-awarded-chip.vue'
import QuoteItemsBtn from '@/components/quotes/quote-items-btn.vue'
import QuoteOutdatedChip from '@/components/quotes/quote-outdated-chip.vue'
import QuoteResponse from '@/components/quotes/quote-response.vue'

export interface Props {
  isLowestPrice?: boolean
  order: Order
  quote: Quote
  showActions?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['click', 'update:order'])

const priceGiven = computed(() => props.quote.response_received === 'price given')
const responseReceived = computed(() => props.quote.response_received as string)
const totalPrice = computed(() => currency(props.quote.total_price))
const unreadMsg = ref(false)

const orderItemCount = computed(() => props.order.orderType?.fromBom ? props.order.skus.length : 0)
const matchedItemCount = computed(() =>
  props.order.skus.filter(orderSku =>
    props.quote.skus.some(quoteSku => quoteSku.reference_identifier === orderSku.internal_identifier)
  ).length
)
const allItemsMatched = computed(() => matchedItemCount.value === orderItemCount.value)

const leadTimeCount = computed(() =>
  props.quote.skus.filter(sku => sku.lead_time != null && sku.lead_time !== '').length
)
const leadTimeText = computed(() =>
  leadTimeCount.value === 1 ? '1 lead time' : `${leadTimeCount.value} lead times`
)

const hasInfo = computed(() => orderItemCount.value > 0 || leadTimeCount.value > 0)

const authStore = useAuthStore()
const user = authStore.user!

const onClick = () => {
  unreadMsg.value = false
  emit('click', props.quote)
}

const { $api, $error } = useNuxtApp()

onMounted(async () => {
  if (user.id === props.order.owner!.id) {
    try {
      unreadMsg.value = await $api.v1.quotes.chat.list(props.quote.id!, { limit: 1, unread: true })
        .then(response => response.count > 0)
    }
    catch (error) {
      $error.report(error)
    }
  }
})
</script>

<style lang="scss" scoped>
.quote-card {
  min-height: 150px;
}

.action-btn {
  flex: 1 0 0;
}

.lead-time-dot {
  width: 3px;
  height: 3px;
}
</style>
